import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Link, useSearchParams } from "react-router-dom";
import { useTickets } from "../../hooks/useTickets";
import { useTicketStatuses } from "../../hooks/useTicketStatuses";
import { DefaultTicketStatusKey } from "../../service/CallCenterApi";
import { getOfficeUsers } from "../../service/CallCenterCalls";
import CallCenterNav from "../../components/call-center/CallCenterNav";
import AppContext from "../../context/AppContext";

const SORT_OPTIONS = [
  { value: "OUTSTANDING_ASC", label: "Outstanding: Low to High" },
  { value: "OUTSTANDING_DESC", label: "Outstanding: High to Low" },
  { value: "PAYMENT_DATE_ASC", label: "Payment Date: Oldest First" },
  { value: "PAYMENT_DATE_DESC", label: "Payment Date: Newest First" },
  { value: "CALLED_AT_ASC", label: "Called At: Oldest First" },
  { value: "CALLED_AT_DESC", label: "Called At: Newest First" },
  { value: "UPDATED_DESC", label: "Recently Updated" },
];

const INITIAL_FILTERS = {
  accountValue: "",
  called: "all",
  statusIds: [],
  minOutstanding: "",
  maxOutstanding: "",
  fromPaymentDate: "",
  toPaymentDate: "",
  sortBy: "UPDATED_DESC",
  assigneeId: "",
};

const INITIAL_PAGINATION = {
  page: 0,
  size: 20,
  totalPages: 0,
  totalElements: 0,
  hasNext: false,
  hasPrevious: false,
};

const isNotFoundError = (error) =>
  error?.response?.status === 404 ||
  error?.responseCode === 404 ||
  /not found/i.test(error?.message || "");

const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const formatDateInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const formatDateTimeLocalInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const toIsoFromDateTimeLocal = (value) => {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
};

const toOptionalNumber = (value) => {
  if (value === "" || value === null || typeof value === "undefined") return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const normalizeStatusOptions = (statuses) => {
  if (!Array.isArray(statuses) || statuses.length === 0) {
    return Object.values(DefaultTicketStatusKey).map((statusKey) => ({
      id: statusKey,
      statusKey,
      label: statusKey,
      name: statusKey,
    }));
  }
  return statuses.map((status, index) => ({
    id: status?.id ?? status?.statusId ?? status?.statusKey ?? `status-${index}`,
    statusKey: status?.statusKey ?? status?.key ?? status?.name ?? "",
    label: status?.name ?? status?.statusKey ?? status?.key ?? "Unknown",
    name: status?.name ?? status?.statusKey ?? status?.key ?? "",
  }));
};

const CallCenterTicketsListPage = () => {
  const { userType } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const highlightTicketId = searchParams.get("ticketId");
  const highlightRowRef = useRef(null);
  const ticketsApi = useTickets();
  const statusesApi = useTicketStatuses();

  const [tickets, setTickets] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [draftUpdates, setDraftUpdates] = useState({});
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [updatingTicketId, setUpdatingTicketId] = useState(null);
  const [refreshingTicketId, setRefreshingTicketId] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeTicketId, setActiveTicketId] = useState(null);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);

  const [statusOptions, setStatusOptions] = useState(
    normalizeStatusOptions([])
  );
  const [officeUsers, setOfficeUsers] = useState([]);

  const canUseAssigneeFilter = useMemo(
    () => ["super_admin", "admin", "project_admin"].includes(String(userType || "").toLowerCase()),
    [userType]
  );

  const statusLookup = useMemo(
    () =>
      statusOptions.reduce((acc, item) => {
        if (item.statusKey) acc[item.statusKey] = item;
        return acc;
      }, {}),
    [statusOptions]
  );
  const activeTicket = useMemo(
    () => tickets.find((ticket) => ticket.id === activeTicketId) || null,
    [tickets, activeTicketId]
  );

  const buildListParams = (nextFilters = filters) => {
    const params = {
      called:
        nextFilters.called === "true"
          ? true
          : nextFilters.called === "false"
            ? false
            : undefined,
      statusIds: nextFilters.statusIds.length ? nextFilters.statusIds : undefined,
      minOutstanding: toOptionalNumber(nextFilters.minOutstanding),
      maxOutstanding: toOptionalNumber(nextFilters.maxOutstanding),
      fromPaymentDate: nextFilters.fromPaymentDate || undefined,
      toPaymentDate: nextFilters.toPaymentDate || undefined,
      sortBy: nextFilters.sortBy || undefined,
      assigneeId: canUseAssigneeFilter ? toOptionalNumber(nextFilters.assigneeId) : undefined,
    };
    const accountValue = nextFilters.accountValue.trim();
    if (accountValue) {
      params.accountNo = accountValue;
    }
    return params;
  };

  const loadTickets = async (
    nextFilters = filters,
    page = pagination.page,
    size = pagination.size
  ) => {
    try {
      const response = await ticketsApi.list({
        ...buildListParams(nextFilters),
        page,
        size,
      });
      const isPaginated = response && typeof response === "object" && Array.isArray(response.items);
      const nextTickets = isPaginated ? response.items : Array.isArray(response) ? response : [];
      setTickets(nextTickets);
      setPagination(
        isPaginated
          ? {
              page: response.page ?? page,
              size: response.size ?? size,
              totalPages: response.totalPages ?? 0,
              totalElements: response.totalElements ?? nextTickets.length,
              hasNext: Boolean(response.hasNext),
              hasPrevious: Boolean(response.hasPrevious),
            }
          : {
              page,
              size,
              totalPages: nextTickets.length ? 1 : 0,
              totalElements: nextTickets.length,
              hasNext: false,
              hasPrevious: false,
            }
      );
      setSelectedTicketIds((prevSelected) =>
        prevSelected.filter((id) => nextTickets.some((ticket) => ticket.id === id))
      );
      setDraftUpdates(
        nextTickets.reduce((acc, ticket) => {
          acc[ticket.id] = {
            status: ticket.status || "",
            remarksComment: ticket.remarksComment || ticket.remarks || "",
            additionalComments: ticket.additionalComments || "",
            targetOutstanding:
              typeof ticket.targetOutstanding === "number" ? String(ticket.targetOutstanding) : "",
            targetPaymentDate: formatDateInput(ticket.targetPaymentDate),
            calledAt: formatDateTimeLocalInput(ticket.calledAt),
          };
          return acc;
        }, {})
      );
    } catch (error) {
      toast.error(error.message || "Failed to load tickets");
    }
  };

  const loadTicketStatuses = async () => {
    try {
      const data = await statusesApi.list();
      setStatusOptions(normalizeStatusOptions(data));
    } catch (error) {
      setStatusOptions(normalizeStatusOptions([]));
      toast.error(error.message || "Failed to load status options");
    }
  };

  const loadOfficeUsers = async () => {
    if (!canUseAssigneeFilter) {
      setOfficeUsers([]);
      return;
    }
    try {
      const users = await getOfficeUsers();
      setOfficeUsers(Array.isArray(users) ? users : []);
    } catch (error) {
      setOfficeUsers([]);
      toast.error(error.message || "Failed to load assignee list");
    }
  };

  useEffect(() => {
    loadTickets(INITIAL_FILTERS, 0, pagination.size);
    loadTicketStatuses();
    loadOfficeUsers();
  }, [canUseAssigneeFilter]);

  useEffect(() => {
    if (!highlightTicketId || tickets.length === 0) return;
    const t = window.setTimeout(() => {
      highlightRowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);
    return () => window.clearTimeout(t);
  }, [highlightTicketId, tickets]);

  const allVisibleTicketIds = useMemo(() => tickets.map((ticket) => ticket.id), [tickets]);
  const allVisibleSelected =
    allVisibleTicketIds.length > 0 &&
    allVisibleTicketIds.every((ticketId) => selectedTicketIds.includes(ticketId));

  const updateDraft = (ticketId, patch) => {
    setDraftUpdates((prev) => ({
      ...prev,
      [ticketId]: {
        ...(prev[ticketId] || {}),
        ...patch,
      },
    }));
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedTicketIds((prev) =>
        prev.filter((ticketId) => !allVisibleTicketIds.includes(ticketId))
      );
      return;
    }
    setSelectedTicketIds((prev) => Array.from(new Set([...prev, ...allVisibleTicketIds])));
  };

  const toggleTicketSelection = (ticketId) => {
    setSelectedTicketIds((prev) =>
      prev.includes(ticketId)
        ? prev.filter((id) => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const openTicketDetails = (ticket) => {
    setActiveTicketId(ticket.id);
  };

  const closeTicketDetails = () => {
    setActiveTicketId(null);
  };

  const handleStatusUpdate = async (ticket) => {
    const draft = draftUpdates[ticket.id] || {};
    try {
      setUpdatingTicketId(ticket.id);
      await ticketsApi.patchStatus({
        id: ticket.id,
        status: draft.status || undefined,
        remarksComment: draft.remarksComment || undefined,
        additionalComments: draft.additionalComments || undefined,
        targetOutstanding: toOptionalNumber(draft.targetOutstanding),
        targetPaymentDate: draft.targetPaymentDate || undefined,
        calledAt: toIsoFromDateTimeLocal(draft.calledAt),
      });

      setTickets((prev) =>
        prev.map((row) =>
          row.id === ticket.id
            ? {
                ...row,
                status: draft.status || row.status,
                remarksComment: draft.remarksComment || row.remarksComment || row.remarks,
                additionalComments:
                  draft.additionalComments || row.additionalComments || "",
                targetOutstanding:
                  typeof toOptionalNumber(draft.targetOutstanding) === "number"
                    ? toOptionalNumber(draft.targetOutstanding)
                    : row.targetOutstanding,
                targetPaymentDate: draft.targetPaymentDate || row.targetPaymentDate,
                calledAt: toIsoFromDateTimeLocal(draft.calledAt) || row.calledAt,
              }
            : row
        )
      );
      toast.success("Ticket status updated");
    } catch (error) {
      if (isNotFoundError(error)) {
        setTickets((prev) => prev.filter((row) => row.id !== ticket.id));
        setSelectedTicketIds((prev) => prev.filter((id) => id !== ticket.id));
        toast.error("Ticket was not found. It may have been deleted.");
        return;
      }
      toast.error(error.message || "Failed to update ticket status");
    } finally {
      setUpdatingTicketId(null);
    }
  };

  const handleRefreshOutstanding = async (ticketId) => {
    try {
      setRefreshingTicketId(ticketId);
      const response = await ticketsApi.refreshOutstanding({
        id: ticketId,
      });
      setTickets((prev) =>
        prev.map((ticket) =>
          ticket.id === ticketId
            ? {
                ...ticket,
                targetOutstanding: response?.liveOutstanding ?? ticket.targetOutstanding,
              }
            : ticket
        )
      );
      toast.success(
        `Outstanding refreshed. Previous: ${response?.previousOutstanding ?? 0}, Live: ${
          response?.liveOutstanding ?? 0
        }, At: ${formatDateTime(response?.refreshedAt)}`
      );
    } catch (error) {
      toast.error(error.message || "Failed to refresh outstanding");
    } finally {
      setRefreshingTicketId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTicketIds.length === 0) {
      toast.info("Select at least one ticket to delete.");
      return;
    }

    try {
      setDeletingSelected(true);
      const response = await ticketsApi.bulkSoftDelete({
        ticketIds: selectedTicketIds,
      });

      const removedIds = new Set([
        ...(Array.isArray(response?.deletedTicketIds) ? response.deletedTicketIds : []),
        ...(Array.isArray(response?.alreadyDeletedTicketIds)
          ? response.alreadyDeletedTicketIds
          : []),
        ...(Array.isArray(response?.notFoundTicketIds) ? response.notFoundTicketIds : []),
      ]);

      setTickets((prev) => prev.filter((ticket) => !removedIds.has(ticket.id)));
      setSelectedTicketIds([]);
      setDraftUpdates((prev) => {
        const next = { ...prev };
        removedIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });

      toast.success(
        `Delete processed. Deleted: ${response?.deletedCount || 0}, already deleted: ${
          response?.alreadyDeletedCount || 0
        }, not found: ${response?.notFoundCount || 0}.`
      );
    } catch (error) {
      toast.error(error.message || "Failed to bulk delete tickets");
    } finally {
      setDeletingSelected(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        <header className="mb-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-[#236DB4] border border-blue-100">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h3m-5 5h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </span>
              Support Tickets
            </h2>
            <Link
              to="/call-center/agent-workspace"
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 text-[#236DB4] shadow-sm px-3 hover:from-blue-100 hover:to-indigo-100 transition-all"
              title="Add New Ticket"
              aria-label="Add New Ticket"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.9} d="M4 7a2 2 0 012-2h3l2 2h7a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V7z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.9} d="M9 13h6m-3-3v6" />
              </svg>
              <span className="text-xs font-semibold tracking-wide">Add New Ticket</span>
            </Link>
          </div>
          <p className="text-sm text-gray-500">Manage and track customer inquiries</p>
          <div className="mt-4">
            <CallCenterNav />
          </div>
        </header>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Ticket Filters</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Showing page {pagination.page + 1} of {Math.max(pagination.totalPages, 1)} •
                {" "}
                {pagination.totalElements} total ticket{pagination.totalElements !== 1 ? "s" : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowAdvancedFilters((prev) => !prev)}
              className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-semibold text-[#236DB4] bg-blue-50 border border-blue-100 rounded-lg hover:bg-blue-100 transition-colors"
            >
              {showAdvancedFilters ? "Hide advanced filters" : "Show advanced filters"}
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            <div className="lg:col-span-5">
              <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                Account No
              </label>
              <div className="flex gap-2">
                <input
                  value={filters.accountValue}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, accountValue: event.target.value }))
                  }
                  onKeyDown={(event) => event.key === "Enter" && loadTickets()}
                  className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg"
                  placeholder="Search account number..."
                />
              </div>
            </div>

            <div className="lg:col-span-2">
              <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                Called
              </label>
              <select
                value={filters.called}
                onChange={(event) => setFilters((prev) => ({ ...prev, called: event.target.value }))}
                className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg"
              >
                <option value="all">All</option>
                <option value="true">Called only</option>
                <option value="false">Not called</option>
              </select>
            </div>

            <div className="lg:col-span-3">
              <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                Assignee
              </label>
              {canUseAssigneeFilter ? (
                <select
                  value={filters.assigneeId}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, assigneeId: event.target.value }))
                  }
                  className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg"
                >
                  <option value="">All assignees</option>
                  {officeUsers.map((user) => (
                    <option key={user.userId} value={String(user.userId)}>
                      {user.name || user.email || `User ${user.userId}`}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="h-11 px-3 flex items-center bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-400">
                  Scoped to current agent
                </div>
              )}
            </div>

            <div className="lg:col-span-2">
              <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(event) => setFilters((prev) => ({ ...prev, sortBy: event.target.value }))}
                className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showAdvancedFilters && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                <div className="lg:col-span-4">
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                    Status IDs
                  </label>
                  <select
                    multiple
                    value={filters.statusIds}
                    onChange={(event) =>
                      setFilters((prev) => ({
                        ...prev,
                        statusIds: Array.from(event.target.selectedOptions).map((option) => option.value),
                      }))
                    }
                    className="w-full h-28 py-2 px-3 bg-gray-50 border border-gray-200 rounded-lg"
                  >
                    {statusOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[11px] text-gray-400">
                    {filters.statusIds.length > 0
                      ? `${filters.statusIds.length} status filter(s) selected`
                      : "Tip: hold Ctrl/Cmd for multi-select"}
                  </p>
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                    Min Outstanding
                  </label>
                  <input
                    type="number"
                    value={filters.minOutstanding}
                    onChange={(event) =>
                      setFilters((prev) => ({ ...prev, minOutstanding: event.target.value }))
                    }
                    className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg"
                    placeholder="0.00"
                  />
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                    Max Outstanding
                  </label>
                  <input
                    type="number"
                    value={filters.maxOutstanding}
                    onChange={(event) =>
                      setFilters((prev) => ({ ...prev, maxOutstanding: event.target.value }))
                    }
                    className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg"
                    placeholder="0.00"
                  />
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                    Payment Date From
                  </label>
                  <input
                    type="date"
                    value={filters.fromPaymentDate}
                    onChange={(event) =>
                      setFilters((prev) => ({ ...prev, fromPaymentDate: event.target.value }))
                    }
                    className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg"
                  />
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">
                    Payment Date To
                  </label>
                  <input
                    type="date"
                    value={filters.toPaymentDate}
                    onChange={(event) =>
                      setFilters((prev) => ({ ...prev, toPaymentDate: event.target.value }))
                    }
                    className="w-full h-11 px-3 bg-gray-50 border border-gray-200 rounded-lg"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => loadTickets(filters, 0, pagination.size)}
              disabled={ticketsApi.loading}
              className="bg-[#236DB4] hover:bg-blue-700 text-white rounded-lg px-5 h-10 text-sm font-medium transition-colors disabled:opacity-50"
            >
              {ticketsApi.loading ? "Loading..." : "Apply Filters"}
            </button>
            <button
              type="button"
              onClick={() => {
                setFilters(INITIAL_FILTERS);
                loadTickets(INITIAL_FILTERS, 0, pagination.size);
              }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-4 h-10 text-sm font-medium transition-colors"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={selectedTicketIds.length === 0 || deletingSelected}
              className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg px-4 h-10 text-xs font-semibold transition-colors disabled:opacity-50 ml-auto"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-7 0h8" />
              </svg>
              {deletingSelected ? "Deleting..." : `Delete selected (${selectedTicketIds.length})`}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {tickets.length === 0 && !ticketsApi.loading ? (
            <div className="p-20 text-center">
              <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">No tickets found</h3>
              <p className="text-gray-500">Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-4 font-semibold text-gray-600">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleSelectAllVisible}
                        aria-label="Select all tickets"
                      />
                    </th>
                    <th className="px-4 py-4 font-semibold text-gray-600">Account No</th>
                    <th className="px-4 py-4 font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-4 font-semibold text-gray-600">Outstanding</th>
                    <th className="px-4 py-4 font-semibold text-gray-600">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {tickets.map((ticket) => {
                    const draft = draftUpdates[ticket.id] || {};
                    const statusValue = draft.status || ticket.status || "";
                    const statusLabel = statusLookup[statusValue]?.label || statusValue || "OPEN";
                    return (
                      <tr
                        key={ticket.id}
                        ref={highlightTicketId && String(ticket.id) === highlightTicketId ? highlightRowRef : undefined}
                        onClick={() => openTicketDetails(ticket)}
                        className={`hover:bg-blue-50/30 transition-colors ${
                          highlightTicketId && String(ticket.id) === highlightTicketId
                            ? "bg-blue-50/80 ring-2 ring-inset ring-[#236DB4]"
                            : ""
                        } cursor-pointer`}
                      >
                        <td className="px-4 py-4 align-top">
                          <input
                            type="checkbox"
                            checked={selectedTicketIds.includes(ticket.id)}
                            onChange={() => toggleTicketSelection(ticket.id)}
                            onClick={(event) => event.stopPropagation()}
                            aria-label={`Select ticket ${ticket.id}`}
                          />
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div className="font-medium text-gray-900">
                            {ticket.accountNo ?? ticket.accountId ?? "—"}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top text-xs text-gray-600 min-w-[180px]">
                          <div className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-[#236DB4] font-semibold border border-blue-100">
                            {statusLabel}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top text-sm text-gray-700">
                          {ticket.targetOutstanding ?? "—"}
                        </td>
                        <td className="px-4 py-4 align-top text-sm text-gray-700 max-w-[260px]">
                          <div className="truncate" title={ticket.remarksComment || ticket.remarks}>
                            {ticket.remarksComment || ticket.remarks || "—"}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-gray-500">
            Page {pagination.page + 1} • {pagination.totalElements} results
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Rows</label>
            <select
              value={pagination.size}
              onChange={(event) => {
                const nextSize = Number(event.target.value) || 20;
                loadTickets(filters, 0, nextSize);
              }}
              className="h-9 px-2 bg-white border border-gray-200 rounded-lg text-xs"
            >
              {[20, 50, 100, 200].map((sizeOption) => (
                <option key={sizeOption} value={sizeOption}>
                  {sizeOption}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => loadTickets(filters, Math.max(0, pagination.page - 1), pagination.size)}
              disabled={!pagination.hasPrevious || ticketsApi.loading}
              className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-xs font-semibold disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => loadTickets(filters, pagination.page + 1, pagination.size)}
              disabled={!pagination.hasNext || ticketsApi.loading}
              className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-xs font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        {activeTicket && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-200">
              <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">
                    Ticket {activeTicket.accountNo ?? activeTicket.accountId ?? activeTicket.id}
                  </h3>
                  <p className="text-xs text-gray-500">Ticket ID: {activeTicket.id}</p>
                </div>
                <button
                  type="button"
                  onClick={closeTicketDetails}
                  className="h-9 px-3 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Ticket Overview</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <div className="text-xs text-gray-500">Priority</div>
                      <div className="font-semibold">{activeTicket.priority || "—"}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <div className="text-xs text-gray-500">Status</div>
                      <div className="font-semibold">{activeTicket.status || "—"}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <div className="text-xs text-gray-500">Target Outstanding</div>
                      <div className="font-semibold">{activeTicket.targetOutstanding ?? "—"}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <div className="text-xs text-gray-500">Payment Date</div>
                      <div className="font-semibold">{formatDateTime(activeTicket.targetPaymentDate)}</div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-5">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Update Ticket</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Status</label>
                      <select
                        value={(draftUpdates[activeTicket.id]?.status || activeTicket.status || "")}
                        onChange={(event) => updateDraft(activeTicket.id, { status: event.target.value })}
                        className="w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                        disabled={updatingTicketId === activeTicket.id}
                      >
                        {statusOptions.map((statusOption) => (
                          <option key={statusOption.id} value={statusOption.statusKey}>
                            {statusOption.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Remarks</label>
                      <input
                        type="text"
                        value={draftUpdates[activeTicket.id]?.remarksComment || ""}
                        onChange={(event) =>
                          updateDraft(activeTicket.id, { remarksComment: event.target.value })
                        }
                        placeholder="Add remark note"
                        className="w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                        disabled={updatingTicketId === activeTicket.id}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Target Outstanding</label>
                      <input
                        type="number"
                        value={draftUpdates[activeTicket.id]?.targetOutstanding ?? ""}
                        onChange={(event) =>
                          updateDraft(activeTicket.id, { targetOutstanding: event.target.value })
                        }
                        placeholder="0.00"
                        className="w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                        disabled={updatingTicketId === activeTicket.id}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Target Payment Date</label>
                      <input
                        type="date"
                        value={draftUpdates[activeTicket.id]?.targetPaymentDate || ""}
                        onChange={(event) =>
                          updateDraft(activeTicket.id, { targetPaymentDate: event.target.value })
                        }
                        className="w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                        disabled={updatingTicketId === activeTicket.id}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Called At</label>
                      <input
                        type="datetime-local"
                        value={draftUpdates[activeTicket.id]?.calledAt || ""}
                        onChange={(event) =>
                          updateDraft(activeTicket.id, { calledAt: event.target.value })
                        }
                        className="w-full h-10 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                        disabled={updatingTicketId === activeTicket.id}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Additional Comments</label>
                      <textarea
                        value={draftUpdates[activeTicket.id]?.additionalComments || ""}
                        onChange={(event) =>
                          updateDraft(activeTicket.id, { additionalComments: event.target.value })
                        }
                        rows={4}
                        placeholder="Add additional comments"
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                        disabled={updatingTicketId === activeTicket.id}
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={() => handleStatusUpdate(activeTicket)}
                      disabled={updatingTicketId === activeTicket.id}
                      className="sm:flex-1 h-10 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 text-sm font-semibold disabled:opacity-50"
                    >
                      {updatingTicketId === activeTicket.id ? "Saving..." : "Save Status Update"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRefreshOutstanding(activeTicket.id)}
                      disabled={refreshingTicketId === activeTicket.id}
                      className="sm:flex-1 h-10 rounded-lg border border-emerald-200 text-emerald-700 bg-emerald-50 text-sm font-semibold disabled:opacity-50"
                    >
                      {refreshingTicketId === activeTicket.id ? "Refreshing..." : "Refresh Live Outstanding"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallCenterTicketsListPage;